import type { Profile } from "@beacon/core/access"
import type { PhotoRefusal } from "@beacon/core/photos"
import { isTimeZone } from "@beacon/core/access/profile-rules"
import type { PickerOption } from "@/design-system/property.ts"

/*
 * A Member's own Profile as the web app edits it: the time zones to choose from, what the
 * first-sign-in check and the Profile page's nudge show, and what an edit saves.
 */

/** The Profile's fields in the order the page shows them. */
export const profileFields = [
  "firstName",
  "lastName",
  "pronouns",
  "jobTitle",
  "location",
  "timeZone",
  "about",
] as const satisfies readonly (keyof Profile)[]

/** Each field as a label names it. */
const labels: Record<keyof Profile, string> = {
  firstName: "First name",
  lastName: "Last name",
  pronouns: "Pronouns",
  jobTitle: "Job title",
  location: "Location",
  timeZone: "Time zone",
  about: "About",
}

/** Each field, and the photo, as the nudge names it when it is blank, after "Add". */
const nudgeWords: Record<keyof Profile | "photo", string> = {
  firstName: "first name",
  lastName: "last name",
  pronouns: "pronouns",
  jobTitle: "job title",
  location: "location",
  timeZone: "time zone",
  about: "a few words about you",
  photo: "photo",
}

/** A zone's offset from UTC as Intl names it now ("GMT+2", "GMT-3:30", "GMT"). */
function offsetOf(zone: string, now: Date) {
  const parts = new Intl.DateTimeFormat("en", { timeZone: zone, timeZoneName: "shortOffset" })
  const offset = parts.formatToParts(now).find(({ type }) => type === "timeZoneName")?.value
  // ICU 78 names a zero offset "GMT+0" where earlier versions name it "GMT"; show it one way.
  return !offset || offset === "GMT+0" ? "GMT" : offset
}

/** An offset as minutes east of UTC, for ordering. */
function minutesOf(offset: string) {
  const [, sign = "+", hours = "0", minutes = "0"] = /GMT([+-])(\d+)(?::(\d+))?/.exec(offset) ?? []
  return (sign === "-" ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
}

/** A zone by its city, and the regions above it: `America/Argentina/Buenos_Aires`. */
function partsOf(zone: string) {
  const words = zone.split("/").map((part) => part.replace(/_/g, " "))
  return { city: words.at(-1) ?? zone, region: words.slice(0, -1).join(" / ") }
}

/** Every zone this browser knows, UTC among them. */
function everyZone() {
  const zones = Intl.supportedValuesOf("timeZone")
  return zones.includes("UTC") ? zones : [...zones, "UTC"]
}

/**
 * Every IANA time zone as a Picker offers it: by its city, its region in the faint tier and its
 * offset now at the end, west to east and then by city. The browser's own zone comes first, under a
 * heading of its own.
 */
export function timeZoneOptions(now: Date, browserZone: string | undefined): PickerOption[] {
  const options = everyZone()
    .map((zone) => {
      const { city, region } = partsOf(zone)
      const offset = offsetOf(zone, now)
      return {
        id: zone,
        label: city,
        ...(region && { secondary: region }),
        trailing: offset,
        keywords: zone,
        minutes: minutesOf(offset),
      }
    })
    .sort((a, b) => a.minutes - b.minutes || a.label.localeCompare(b.label))
    .map(({ minutes: _, ...option }) => option)
  const browser = options.find(({ id }) => id === browserZone)
  return [
    ...(browser ? [{ ...browser, group: "Your browser's" }] : []),
    ...options
      .filter(({ id }) => id !== browserZone)
      .map((option) => ({ ...option, group: "Every time zone" })),
  ]
}

/** A zone as a field shows it: its city and its offset now, or "Not set". */
export function timeZoneShown(zone: string | null, now: Date) {
  return zone ? `${partsOf(zone).city} (${offsetOf(zone, now)})` : "Not set"
}

/** The zone this browser is in, as it says. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/** A Profile's time zone, or else the browser's when it is one IANA names, or else none. */
export function seededTimeZone(saved: string | null, browser: string | undefined) {
  return saved ?? (browser && isTimeZone(browser) ? browser : null)
}

/** Whether the first-sign-in check still shows: until the Member confirms or skips it. */
export const checkShows = (me: { profileChecked: boolean }) => !me.profileChecked

/** The fields of a Profile that are blank, and the photo when there is none, in page order. */
export function blankFields(profile: Profile, hasPhoto: boolean): (keyof Profile | "photo")[] {
  return [
    ...profileFields.filter((field) => !profile[field]?.trim()),
    ...(hasPhoto ? [] : ["photo" as const]),
  ]
}

/** Words as a sentence lists them: "a, b and c". */
function listed(words: readonly string[]) {
  return words.length < 2
    ? (words[0] ?? "")
    : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`
}

/**
 * The Profile page's quiet nudge about what is blank, or none once nothing is. "your" leads the
 * list, unless it starts with the few words about them, which say "you" themselves.
 */
export function nudgeOf(blanks: readonly (keyof Profile | "photo")[]) {
  if (!blanks.length) return undefined
  const words = blanks.map((field, index) => {
    const word = nudgeWords[field]
    return index === 0 && field !== "about" ? `your ${word}` : word
  })
  return `Add ${listed(words)}, so colleagues know who you are.`
}

/** A Profile as its form holds it: every field as text, a blank one empty. */
export type ProfileDraft = Record<(typeof profileFields)[number], string>

export const draftOf = (profile: Profile): ProfileDraft =>
  Object.fromEntries(profileFields.map((field) => [field, profile[field] ?? ""])) as ProfileDraft

/** The fields a draft changes from what is saved, comparing each trimmed, a blank one as none. */
function changed(saved: Profile, draft: ProfileDraft) {
  return profileFields.filter((field) => (draft[field].trim() || null) !== saved[field])
}

/** What saving a draft sends: each field it changes, a cleared one as `null`. */
export function editOf(saved: Profile, draft: ProfileDraft) {
  return Object.fromEntries(
    changed(saved, draft).map((field) => [field, draft[field].trim() || null]),
  ) as Partial<Profile>
}

/** What a draft changes, as the SaveBar says it ("Pronouns and about"), or none. */
export function changedFields(saved: Profile, draft: ProfileDraft) {
  const fields = changed(saved, draft)
  if (!fields.length) return undefined
  const words = fields.map((field, index) =>
    index === 0 ? labels[field] : labels[field].toLowerCase(),
  )
  return listed(words)
}

const unreadable = "Beacon couldn't read that photo. Choose a JPEG, PNG, WebP or iPhone photo."

// Keyed by core's own union, and the store refusing the bytes, so a refusal `photos` gains is a type
// error here until it has words.
const photoRefusals: Record<PhotoRefusal | "upload-failed", string> = {
  "too-large": "That photo is over 10 MB. Choose a smaller one.",
  unreadable,
  "invalid-photo": unreadable,
  "upload-failed": "Your photo didn't upload. Try again.",
  "not-found": "That upload has expired. Choose the photo again.",
  "not-yours": "Only they, or an Admin, can remove that photo.",
}

/** Why a photo wasn't kept, in words, or a plain apology for anything else. */
export function describePhotoRefusal(refusal: string) {
  return (
    photoRefusals[refusal as keyof typeof photoRefusals] ??
    "Beacon couldn't change your photo. Try again."
  )
}
